import React from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Icon from '../../../components/dashboard/DashboardIcons';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
export const money = (n) => new Intl.NumberFormat('ar-SA', { style: 'currency', currency: 'SAR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
export const fmtDate = (d) => (d ? new Intl.DateTimeFormat('ar-SA-u-ca-gregory', { dateStyle: 'medium' }).format(new Date(d)) : '—');
export const errMsg = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || '').filter(Boolean).join('، ') || fallback;
  return fallback;
};
export const inputCls = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-[inset_0_1px_2px_rgba(15,23,42,0.05)] transition-colors placeholder:text-slate-400 hover:border-slate-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/15 focus:outline-none';

export const EXPENSE_CATS = { salaries: 'رواتب', employee_advance: 'سلفة موظف', maintenance: 'صيانة', utilities: 'مرافق', equipment: 'معدات', marketing: 'تسويق', transportation: 'مواصلات', food: 'إعاشة', medical: 'طبي', training: 'تدريب', other: 'أخرى' };
export const REVENUE_CATS = { membership: 'اشتراكات', sponsorship: 'رعاية', tickets: 'تذاكر', merchandise: 'مبيعات', training_fees: 'رسوم تدريب', donations: 'تبرعات', other: 'أخرى' };
export const PAY_METHODS = { cash: 'نقدي', bank_transfer: 'تحويل بنكي', credit_card: 'بطاقة', check: 'شيك' };
export const STATUS_LABELS = { pending: 'معلق', approved: 'معتمد', paid: 'مدفوع', rejected: 'مرفوض', voided: 'ملغى', unpaid: 'غير مدفوعة', partial: 'مدفوعة جزئياً', cancelled: 'ملغاة', overdue: 'متأخرة', draft: 'مسودة', sent: 'مُرسل', accepted: 'مقبول', expired: 'منتهي' };
const STATUS_COLORS = { pending: 'bg-amber-50 text-amber-700', approved: 'bg-blue-50 text-blue-700', paid: 'bg-teal-50 text-teal-700', rejected: 'bg-red-50 text-red-600', voided: 'bg-slate-100 text-slate-500', unpaid: 'bg-amber-50 text-amber-700', partial: 'bg-blue-50 text-blue-700', cancelled: 'bg-slate-100 text-slate-500', overdue: 'bg-red-50 text-red-600', draft: 'bg-slate-100 text-slate-500', sent: 'bg-blue-50 text-blue-700', accepted: 'bg-teal-50 text-teal-700', expired: 'bg-red-50 text-red-600' };

export const StatusBadge = ({ status, overdue }) => {
  const effective = overdue ? 'overdue' : status;
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[effective] || 'bg-slate-100 text-slate-600'}`}>{STATUS_LABELS[effective] || effective}</span>;
};

export const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-bold text-slate-700">{label}{required && <span className="text-red-500"> *</span>}</span>
    {children}
  </label>
);

export const Modal = ({ open, title, onClose, children, wide }) => {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative max-h-[90vh] w-full ${wide ? 'max-w-2xl' : 'max-w-md'} overflow-y-auto rounded-xl bg-white p-6 shadow-2xl`}>
        <h3 className="mb-4 text-base font-bold text-slate-900">{title}</h3>
        {children}
      </div>
    </div>
  );
};

export const PageHead = ({ title, desc, action }) => (
  <div className="flex flex-wrap items-center justify-between gap-3">
    <div>
      <h2 className="text-lg font-bold text-slate-900">{title}</h2>
      {desc && <p className="text-sm text-slate-500">{desc}</p>}
    </div>
    {action}
  </div>
);

export const PrimaryBtn = ({ children, ...props }) => (
  <button {...props} className="flex items-center gap-2 rounded-lg bg-[#16233b] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1e2f4e] disabled:opacity-50">{children}</button>
);

export const Paginator = ({ page, total, limit, onChange }) => {
  const pages = Math.max(1, Math.ceil(total / limit));
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 py-3" data-testid="paginator">
      <button disabled={page <= 1} onClick={() => onChange(page - 1)} className="rounded-lg border border-slate-200 px-3 py-1 text-sm disabled:opacity-40">السابق</button>
      <span className="text-sm text-slate-500">{page} / {pages}</span>
      <button disabled={page >= pages} onClick={() => onChange(page + 1)} className="rounded-lg border border-slate-200 px-3 py-1 text-sm disabled:opacity-40">التالي</button>
    </div>
  );
};

export const FinanceEmpty = ({ text }) => (
  <div className="flex flex-col items-center py-8 text-center">
    <span className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400"><Icon name="banknotes" className="w-5 h-5" /></span>
    <p className="text-sm text-slate-500">{text}</p>
  </div>
);

export const exportExcel = (rows, filename) => {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

export const printArea = () => window.print();
export const PrintStyle = () => (
  <style>{`
    @page { size: A4; margin: 12mm; }
    @media print {
      body * { visibility: hidden !important; position: static !important; }
      .print-area, .print-area * { visibility: visible !important; }
      .print-area { position: absolute !important; top: 0 !important; left: 0 !important; width: 100% !important; max-width: 186mm !important; margin: 0 !important; background: white; padding: 24px; }
      .print-area table { width: 100%; border-collapse: collapse; }
      .print-area thead { display: table-header-group; }
      .print-area tfoot { display: table-footer-group; }
      .print-area tr { break-inside: avoid; page-break-inside: avoid; }
      .print-area img { break-inside: avoid; page-break-inside: avoid; }
    }
  `}</style>
);

const PX_PER_MM = 96 / 25.4;
const PAGE_SIZES_MM = {
  A4: { portrait: { w: 210, h: 297 }, landscape: { w: 297, h: 210 } },
  A3: { portrait: { w: 297, h: 420 }, landscape: { w: 420, h: 297 } },
  letter: { portrait: { w: 216, h: 279 }, landscape: { w: 279, h: 216 } },
};

// مكوّن مشترك لتنزيل PDF بمقاس ثابت مستقل عن حجم/تكبير الشاشة الحالية، مع تكرار رأس الجدول وتثبيت التذييل أسفل كل صفحة ومنع قص الصفوف
export const exportPdf = async (element, filename, opts = {}) => {
  if (!element) return;
  const orientation = opts.orientation === 'landscape' ? 'landscape' : 'portrait';
  const paperSize = PAGE_SIZES_MM[opts.paperSize] ? opts.paperSize : 'A4';
  const marginMm = 10;
  const { w: pageWmm, h: pageHmm } = PAGE_SIZES_MM[paperSize][orientation];
  const contentWmm = pageWmm - marginMm * 2;
  const contentHmm = pageHmm - marginMm * 2;
  const contentWpx = Math.round(contentWmm * PX_PER_MM);
  const RENDER_SCALE = 3.5;

  const wrapper = document.createElement('div');
  wrapper.style.cssText = `position:fixed;top:0;left:-99999px;width:${contentWpx}px;background:#ffffff;z-index:-1;direction:rtl;`;
  const clone = element.cloneNode(true);
  clone.style.position = 'static';
  clone.style.top = 'auto';
  clone.style.left = 'auto';
  clone.style.width = '100%';
  wrapper.appendChild(clone);
  // ضبط ارتفاع خط الأساس للجداول أثناء التصدير فقط (لا يغيّر الشاشة)
  const normalizeStyle = document.createElement('style');
  normalizeStyle.textContent = 'table{table-layout:auto;} table th, table td{line-height:1.5 !important;}';
  wrapper.appendChild(normalizeStyle);
  document.body.appendChild(wrapper);
  // انتظار تحميل الخطوط بالكامل قبل أي تصوير — يمنع قياس ارتفاع خاطئ/قصّاً غير ثابت في النص عند استخدام خط مخصّص (Cairo/Amiri) لم يكتمل تحميله بعد
  if (document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch (e) { /* تجاهل */ }
  }
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

  // html2canvas لا يحترم vertical-align:middle على خلايا الجدول (قصور معروف في المكتبة)، فنغلّف محتوى كل خلية
  // بعنصر flex بارتفاع صف مساوٍ للارتفاع الفعلي المُقاس من المتصفح لمحاذاة رأسية سليمة — مع مطابقة flex-start/flex-end
  // للجهة اليمنى/اليسرى الفعليتين حسب اتجاه الخلية (rtl تقابل flex-start يمين الشاشة، ltr تقابل flex-start يسارها)
  Array.from(clone.querySelectorAll('table tr')).forEach((tr) => {
    const rowH = tr.getBoundingClientRect().height;
    Array.from(tr.children).forEach((cell) => {
      if (cell.tagName !== 'TD' && cell.tagName !== 'TH') return;
      const cs = window.getComputedStyle(cell);
      const padTop = parseFloat(cs.paddingTop) || 0;
      const padBottom = parseFloat(cs.paddingBottom) || 0;
      const innerH = Math.max(0, rowH - padTop - padBottom);
      const dir = cs.direction === 'ltr' ? 'ltr' : 'rtl';
      const ta = cs.textAlign;
      let visualSide = 'right';
      if (ta === 'center') visualSide = 'center';
      else if (ta === 'left' || (ta === 'start' && dir === 'ltr') || (ta === 'end' && dir === 'rtl')) visualSide = 'left';
      else if (ta === 'right' || (ta === 'start' && dir === 'rtl') || (ta === 'end' && dir === 'ltr')) visualSide = 'right';
      let justify = 'center';
      if (visualSide !== 'center') {
        justify = dir === 'rtl'
          ? (visualSide === 'right' ? 'flex-start' : 'flex-end')
          : (visualSide === 'right' ? 'flex-end' : 'flex-start');
      }
      const wrapEl = document.createElement('div');
      wrapEl.setAttribute('dir', dir);
      wrapEl.style.cssText = `display:flex;align-items:center;justify-content:${justify};height:${innerH}px;width:100%;box-sizing:border-box;`;
      while (cell.firstChild) wrapEl.appendChild(cell.firstChild);
      cell.appendChild(wrapEl);
    });
  });

  // استخراج التذييل ليُثبَّت أسفل هامش كل صفحة كصورة مستقلة (تحافظ على تشكيل الحروف العربية) بدل تركه يتبع تدفق المحتوى
  // يُنقل التذييل إلى غلاف مستقل صغير أعلى الصفحة قبل التصوير — تفادياً لخلل قص الارتفاع في html2canvas عند تصوير
  // عنصر عميق داخل مستند طويل جداً (المحتوى الكامل غير المُقسَّم لصفحات في هذه المرحلة قد يتجاوز آلاف البكسلات ارتفاعاً)
  const footerEl = clone.querySelector('[data-testid="report-footer"]');
  const showPageNumber = footerEl?.dataset?.showPageNumber === '1';
  let footerCanvas = null;
  let footerHmm = 0;
  if (footerEl && footerEl.parentNode) {
    const footerWrapper = document.createElement('div');
    footerWrapper.style.cssText = `position:fixed;top:0;left:-99999px;width:${contentWpx}px;background:#ffffff;direction:rtl;`;
    const footerClone = footerEl.cloneNode(true);
    footerWrapper.appendChild(footerClone);
    document.body.appendChild(footerWrapper);
    footerCanvas = await html2canvas(footerClone, { scale: RENDER_SCALE, useCORS: true, backgroundColor: '#ffffff', logging: false, height: footerClone.scrollHeight, windowHeight: footerClone.scrollHeight });
    document.body.removeChild(footerWrapper);
    footerHmm = (footerCanvas.height / footerCanvas.width) * contentWmm;
    footerEl.parentNode.removeChild(footerEl);
  }
  const footerGapMm = footerHmm ? 4 : 0;
  const effectiveContentHmm = Math.max(20, contentHmm - footerHmm - footerGapMm);
  const contentHpx = effectiveContentHmm * PX_PER_MM;

  try {
    const table = clone.querySelector('table');
    const thead = table ? table.querySelector('thead') : null;
    let theadCanvas = null;
    let theadHpx = 0;
    if (thead) {
      theadCanvas = await html2canvas(thead, { scale: RENDER_SCALE, useCORS: true, backgroundColor: '#ffffff', logging: false });
      theadHpx = thead.getBoundingClientRect().height;
    }

    const fullCanvas = await html2canvas(clone, { scale: RENDER_SCALE, useCORS: true, backgroundColor: '#ffffff', logging: false });
    const wrapperRect = wrapper.getBoundingClientRect();
    const scaleRatio = fullCanvas.width / wrapperRect.width;
    const containerTop = wrapperRect.top;

    const rowBoundaries = table
      ? Array.from(table.querySelectorAll('tbody > tr')).map((tr) => {
          const r = tr.getBoundingClientRect();
          return { top: r.top - containerTop, bottom: r.bottom - containerTop };
        })
      : [];
    let tableTop = null;
    let tableBottom = null;
    if (table) {
      const tRect = table.getBoundingClientRect();
      tableTop = tRect.top - containerTop;
      tableBottom = tRect.bottom - containerTop;
    }

    // تمرير أول لحساب حدود كل صفحة وعددها الكلي مسبقاً (مطلوب لترقيم الصفحات في التذييل المثبّت)
    const totalHpx = wrapperRect.height;
    const slices = [];
    {
      let cursorPx = 0;
      let first = true;
      while (cursorPx < totalHpx - 1) {
        const availableForThisPage = first ? contentHpx : contentHpx - theadHpx;
        let sliceEnd = cursorPx + Math.max(availableForThisPage, 20);
        if (sliceEnd < totalHpx && rowBoundaries.length) {
          // opts.looseRowBreak: فقط نمنع قصّ صف منتصف الطريق فعلاً، ولا نقلّص الصفحة بلا داعٍ إذا لم يوجد خطر قصّ
          // (يحمي من فجوة فارغة على الصفحة الأولى وصفحة ثانية زائدة بعد نهاية الجدول مباشرة — خاص بعروض السعر)
          if (opts.looseRowBreak) {
            const overlapping = rowBoundaries.find((b) => b.top < sliceEnd && b.bottom > sliceEnd);
            if (overlapping && overlapping.top > cursorPx) sliceEnd = overlapping.top;
          } else {
            const fitting = rowBoundaries.filter((b) => b.top >= cursorPx - 0.5 && b.bottom <= sliceEnd + 0.5);
            if (fitting.length) {
              sliceEnd = fitting[fitting.length - 1].bottom;
            } else {
              const overlapping = rowBoundaries.find((b) => b.top < sliceEnd && b.bottom > sliceEnd);
              if (overlapping && overlapping.top > cursorPx) sliceEnd = overlapping.top;
            }
          }
        } else if (sliceEnd >= totalHpx) {
          sliceEnd = totalHpx;
        }
        slices.push({ start: cursorPx, end: sliceEnd, first });
        cursorPx = sliceEnd;
        first = false;
      }
    }
    const totalPages = slices.length || 1;

    const pdf = new jsPDF(orientation === 'landscape' ? 'l' : 'p', 'mm', paperSize.toLowerCase());

    slices.forEach((slice, idx) => {
      if (idx > 0) pdf.addPage();
      let yOffsetMm = marginMm;
      // لا نكرر رأس الجدول في عروض السعر إلا إذا كانت هذه الصفحة تكمل فعلاً صفوفاً من الجدول
      // (يمنع ظهور رأس جدول يتيم بلا صفوف في صفحة تالية تحتوي فقط على الملخص/الملاحظات)
      const repeatsTableRows = !opts.looseRowBreak || (tableTop !== null && slice.start < tableBottom - 0.5 && slice.start >= tableTop - 0.5);
      if (!slice.first && theadCanvas && repeatsTableRows) {
        const theadHmm = (theadCanvas.height / theadCanvas.width) * contentWmm;
        pdf.addImage(theadCanvas.toDataURL('image/png'), 'PNG', marginMm, marginMm, contentWmm, theadHmm, undefined, 'SLOW');
        yOffsetMm = marginMm + theadHmm;
      }

      const sliceTopPx = Math.max(0, Math.round(slice.start * scaleRatio));
      const sliceHeightPx = Math.max(1, Math.round((slice.end - slice.start) * scaleRatio));
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = fullCanvas.width;
      sliceCanvas.height = sliceHeightPx;
      const ctx = sliceCanvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
      ctx.drawImage(fullCanvas, 0, sliceTopPx, fullCanvas.width, sliceHeightPx, 0, 0, fullCanvas.width, sliceHeightPx);
      const sliceHmm = (sliceHeightPx / fullCanvas.width) * contentWmm;
      pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', marginMm, yOffsetMm, contentWmm, sliceHmm, undefined, 'MEDIUM');

      if (footerCanvas) {
        const footerYmm = pageHmm - marginMm - footerHmm;
        pdf.addImage(footerCanvas.toDataURL('image/png'), 'PNG', marginMm, footerYmm, contentWmm, footerHmm, undefined, 'SLOW');
        if (showPageNumber && totalPages > 1) {
          pdf.setFontSize(8);
          pdf.setTextColor(100, 116, 139);
          pdf.text(`${idx + 1} / ${totalPages}`, marginMm, pageHmm - marginMm - footerHmm / 2 + 1, { align: 'left' });
        }
      }
    });

    pdf.save(`${filename}.pdf`);
  } finally {
    document.body.removeChild(wrapper);
  }
};

// تنزيل عنصر كصورة PNG واحدة (بدون تقسيم صفحات) — تُستخدم لعروض السعر
export const exportImage = async (element, filename) => {
  if (!element) return;
  const widthPx = Math.round(element.getBoundingClientRect().width) || 794;
  const wrapper = document.createElement('div');
  wrapper.style.cssText = `position:fixed;top:0;left:-99999px;width:${widthPx}px;background:#ffffff;z-index:-1;direction:rtl;`;
  const clone = element.cloneNode(true);
  clone.style.position = 'static';
  clone.style.top = 'auto';
  clone.style.left = 'auto';
  clone.style.width = '100%';
  wrapper.appendChild(clone);
  document.body.appendChild(wrapper);
  if (document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch (e) { /* تجاهل */ }
  }
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  try {
    const canvas = await html2canvas(clone, { scale: 3, useCORS: true, backgroundColor: '#ffffff', logging: false });
    const link = document.createElement('a');
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    document.body.removeChild(wrapper);
  }
};
